import { SkipToken, skipToken } from "@reduxjs/toolkit/query";
import { sharedLib } from "@shared/services/shared.service";
import {
  BotConfigurationType,
  BotType,
  ClientData,
  DCABotPageSource,
  GetBotCreateGeneralDataRequest,
  SubGroup,
  WorkspaceType,
} from "@shared/types";
import {
  LEVERAGE_EXCHANGES_TAG,
  NON_LEVERAGE_EXCHANGES_TAG,
} from "@shared/common/constants";
import {
  useClientsData,
  useIsSubUser,
  useQueryWithRetries,
  useSymbolsMetadata,
} from "@shared/utils/hooks/misc";
import {
  BotFormType,
  MultiValue,
} from "@shared/features/bots/BotForm/common/types";
import { useDefaultSubGroupConfiguration } from "@shared/features/bots/BotForm/common/hooks";
import { useSelector } from "react-redux";
import { selectWorkspace } from "@shared/slices/common.slice";
import { useMemo } from "react";
import { isSymbol } from "@shared/types/utils";
import { xor } from "lodash";
import useExchangeToSymbolPricesQueries from "@shared/features/bots/BotForm/common/hooks/useExchangeToSymbolPricesQueries";

interface useBotFormQueriesProps {
  isEdit: boolean;
  isDuplicate: boolean;
  isPublic: boolean;
  botID?: number;
  backtestingBotId?: number;
  botIDs?: number[];
  botUUID?: string;
  account?: ClientData;
  accounts: ClientData[] | MultiValue;
  exchanges?: string[] | MultiValue;
  subGroup?: SubGroup;
  symbols: string[];
  useSymbolFromAlert?: boolean | MultiValue;
  botType: BotType;
  botFormType?: BotFormType;
  isChannelConfiguration?: boolean;
  isDemoCopyPublic?: boolean;
  isCopyToWorkspace?: boolean;
  showBacktesting?: boolean;
  configurationType?: BotConfigurationType;
  subGroupId?: number;
}

const useBotFormQueries = ({
  isDuplicate,
  botID,
  backtestingBotId,
  botIDs,
  isPublic,
  isEdit,
  botUUID,
  account,
  subGroup,
  symbols,
  useSymbolFromAlert,
  accounts,
  exchanges,
  botType,
  botFormType,
  isChannelConfiguration,
  isDemoCopyPublic,
  isCopyToWorkspace,
  showBacktesting,
  configurationType,
  subGroupId,
}: useBotFormQueriesProps) => {
  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const workspace = useSelector(selectWorkspace);
  const symbol = symbols?.[0];
  const isSubUser = useIsSubUser();

  const isDemoQuery = () => {
    if (isCopyToWorkspace) {
      const originalWorkspace =
        workspace === WorkspaceType.DEMO
          ? WorkspaceType.USER
          : WorkspaceType.DEMO;
      return originalWorkspace === WorkspaceType.DEMO;
    }
    return undefined;
  };

  const getBotDataQuery = sharedLib.sharedApi.bots.useGetBotDataQuery(
    (isEdit || isDuplicate) && botID
      ? {
          dca_bot_id: botID as number,
          is_channel_configuration: isChannelConfiguration,
          is_demo_query: isDemoQuery(),
        }
      : skipToken
  );
  const getDefaultConfigurationDataQuery =
    sharedLib.sharedApi.bots.useGetSubGroupDefaultConfigurationQuery(
      isChannelConfiguration && subGroupId
        ? {
            sub_group_id: subGroupId,
            exchange:
              configurationType === BotConfigurationType.SPOT
                ? NON_LEVERAGE_EXCHANGES_TAG
                : LEVERAGE_EXCHANGES_TAG,
          }
        : skipToken
    );
  const getPublicBotDataQuery =
    sharedLib.sharedApi.bots.useGetPublicBotDataQuery(
      botUUID && isPublic
        ? { dca_bot_uuid: botUUID, is_demo_query: !!isDemoCopyPublic }
        : skipToken
    );
  const getBacktestBotDataQuery = sharedLib.sharedApi.bots.useGetBotDataQuery(
    showBacktesting && backtestingBotId
      ? {
          dca_bot_id: backtestingBotId,
          is_backtesting: true,
          is_channel_configuration: false,
        }
      : skipToken
  );
  const getClientsListQuery =
    sharedLib.sharedApi.configuration.useGetClientsListQuery(
      isChannelConfiguration ? skipToken : undefined
    );
  const { activeClientIds, getClientsListStatus } = useClientsData();

  const { currentData: backtestBotGeneralData } = getBacktestBotDataQuery;
  const { data: editBotGeneralData } = getBotDataQuery;
  const { currentData: publicBotGeneralData } = getPublicBotDataQuery;

  const originalAccountExchange =
    backtestBotGeneralData?.data.exchange ||
    editBotGeneralData?.data.exchange ||
    publicBotGeneralData?.exchange;
  const originalExchanges = editBotGeneralData?.data.exchanges;

  const editOrPublicBotValuesChanged =
    (!!account && originalAccountExchange !== account.exchange) ||
    (!!exchanges &&
      !isSymbol(exchanges) &&
      !!xor(exchanges, originalExchanges).length);
  const isEmptyAccounts =
    getClientsListStatus.isSuccess && !activeClientIds.length;
  const useBotCreateGeneralData =
    !isChannelConfiguration &&
    !isBulkCreate &&
    (account || isEmptyAccounts || isTradingViewSignalsBot) &&
    ((!botID && !botUUID) || editOrPublicBotValuesChanged);

  const getBotCreateGeneralDataArgs = useMemo(() => {
    let getBotCreateGeneralData: GetBotCreateGeneralDataRequest | SkipToken =
      skipToken;
    if (useBotCreateGeneralData) {
      getBotCreateGeneralData = { bot_type: botType, include_leverage: true };
      if (isChannelConfiguration) {
        getBotCreateGeneralData.is_channel_configuration = true;
      } else if (isTradingViewSignalsBot) {
        if (!isSymbol(exchanges) && exchanges?.length) {
          getBotCreateGeneralData.exchanges = exchanges;
        } else {
          getBotCreateGeneralData = skipToken;
        }
      } else if (isEmptyAccounts) {
        getBotCreateGeneralData.include_leverage = false;
      } else {
        getBotCreateGeneralData.account_id = account?.client_id;
      }
    }
    return getBotCreateGeneralData;
  }, [
    useBotCreateGeneralData,
    isChannelConfiguration,
    botType,
    account,
    exchanges,
    isEmptyAccounts,
    isTradingViewSignalsBot,
  ]);
  const getBotCreateGeneralDataQuery =
    sharedLib.sharedApi.bots.useGetBotCreateGeneralDataQuery(
      getBotCreateGeneralDataArgs
    );

  const getBotBulkCreateGeneralDataArgs = useMemo(() => {
    if (!isBulkCreate) return skipToken;
    return activeClientIds?.length
      ? {
          bot_type: botType,
          account_ids: activeClientIds,
        }
      : skipToken;
  }, [isBulkCreate, activeClientIds]);

  const getBotBulkCreateGeneralDataQuery =
    sharedLib.sharedApi.bots.useGetBotBulkCreateGeneralDataQuery(
      getBotBulkCreateGeneralDataArgs
    );

  const getBotsNamesQuery = sharedLib.sharedApi.bots.useGetBotsNamesQuery(
    botType && !isChannelConfiguration ? { bot_type: botType } : skipToken
  );
  const getSymbolsMetadataStatus = useQueryWithRetries(
    sharedLib.sharedApi.common.useGetSymbolsMetadataQuery
  );
  const getGroupsDataQuery = sharedLib.sharedApi.settings.useGetGroupsDataQuery(
    (isSignalsBot && !isChannelConfiguration) ||
      botType === BotType.TRADING_VIEW_SIGNALS
      ? undefined
      : skipToken
  );

  sharedLib.sharedApi.marketplace.useGetMarketplaceInfoQuery(
    isSignalsBot && !isChannelConfiguration && !isSubUser
      ? undefined
      : skipToken
  );
  useDefaultSubGroupConfiguration({
    subGroupId: subGroup?.id,
    exchange: account?.exchange,
  });
  const getBotsDataQuery = sharedLib.sharedApi.bots.useGetBotsDataQuery(
    botIDs && botType
      ? {
          bot_type: botType,
          bot_ids: botIDs,
          bot_page_source: DCABotPageSource.CREATE_FORM,
          include_archived: false,
        }
      : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const { exchangeToSymbols, getSymbolMetadata } = useSymbolsMetadata();

  const isSymbolValid = useMemo(() => {
    if (isTradingViewSignalsBot) {
      return (
        !!symbols &&
        symbols.length > 0 &&
        (isSymbol(exchanges) ||
          exchanges?.some((exchange) =>
            exchangeToSymbols?.[exchange]?.has(symbol)
          ))
      );
    }
    return (
      account &&
      !!symbols &&
      symbols.length > 0 &&
      exchangeToSymbols?.[account.exchange]?.has(symbol)
    );
  }, [isTradingViewSignalsBot, account, symbols, exchangeToSymbols, exchanges]);

  const { getExchangeSymbolPriceQuery, getExchangeToSymbolPricesQuery } =
    useExchangeToSymbolPricesQueries({
      account,
      symbols,
      useSymbolFromAlert,
      accounts,
      exchanges,
      botType,
      botFormType,
      isChannelConfiguration,
      isSymbolValid,
      botsData: getBotsDataQuery?.currentData?.data,
    });

  const symbolMetadata = getSymbolMetadata(symbol, account?.exchange);

  return {
    getBotDataQuery,
    getPublicBotDataQuery,
    getBotCreateGeneralDataQuery,
    getBotBulkCreateGeneralDataQuery,
    isSymbolValid,
    getBotsNamesQuery,
    getClientsListQuery,
    getExchangeSymbolPriceQuery,
    getSymbolsMetadataStatus,
    getGroupsDataQuery,
    symbolMetadata,
    useBotCreateGeneralData,
    getBotsDataQuery,
    getBacktestBotDataQuery,
    getDefaultConfigurationDataQuery,
    getExchangeToSymbolPricesQuery,
  };
};

export default useBotFormQueries;
